import React from 'react';
import PropTypes from 'prop-types';
import './ProgressRing.scss';

/**
 * Design System ProgressRing Component
 *
 * A circular progress indicator with percentage display.
 * Perfect for showing profile completion, skill levels, and other percentage-based metrics.
 *
 * @example
 * <ProgressRing
 *   percentage={75}
 *   size={120}
 *   label="Profile Complete"
 *   color="#FED230"
 * />
 */
const ProgressRing = ({
    percentage = 0,
    size = 100,
    strokeWidth = 8,
    color = '#FED230',
    backgroundColor = '#F5F5F5',
    label,
    showPercentage = true,
    animated = true,
    className = '',
    style = {},
    ...props
}) => {
    const [displayPercentage, setDisplayPercentage] = React.useState(animated ? 0 : percentage);

    React.useEffect(() => {
        if (animated) {
            // Trigger CSS transition by setting target after mount
            const raf = requestAnimationFrame(() => {
                setDisplayPercentage(percentage);
            });
            return () => cancelAnimationFrame(raf);
        } else {
            setDisplayPercentage(percentage);
        }
    }, [percentage, animated]);

    // Calculate SVG circle parameters
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (displayPercentage / 100) * circumference;

    const baseClassName = 'ds-progress-ring';
    const combinedClassName = [baseClassName, className].filter(Boolean).join(' ');

    return (
        <div
            className={combinedClassName}
            style={{ width: size, height: size, ...style }}
            {...props}
        >
            <svg
                className="ds-progress-ring__svg"
                width={size}
                height={size}
            >
                {/* Background circle */}
                <circle
                    className="ds-progress-ring__circle-bg"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={backgroundColor}
                    strokeWidth={strokeWidth}
                />

                {/* Progress circle */}
                <circle
                    className="ds-progress-ring__circle"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>

            {/* Center content */}
            <div className="ds-progress-ring__content">
                {showPercentage && (
                    <div className="ds-progress-ring__percentage">
                        {Math.round(displayPercentage)}%
                    </div>
                )}
                {label && (
                    <div className="ds-progress-ring__label">
                        {label}
                    </div>
                )}
            </div>
        </div>
    );
};

ProgressRing.propTypes = {
    /** Progress percentage (0-100) */
    percentage: PropTypes.number.isRequired,

    /** Size of the ring in pixels */
    size: PropTypes.number,

    /** Width of the progress stroke */
    strokeWidth: PropTypes.number,

    /** Color of the progress ring */
    color: PropTypes.string,

    /** Background ring color */
    backgroundColor: PropTypes.string,

    /** Label to display below percentage */
    label: PropTypes.string,

    /** Whether to show the percentage number */
    showPercentage: PropTypes.bool,

    /** Whether to animate the progress on mount */
    animated: PropTypes.bool,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object
};

export default ProgressRing;
